import { useState, useEffect } from 'react';
import * as FileSystem from 'expo-file-system';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import NetInfo from '@react-native-community/netinfo';

const DOCUMENTS_STORAGE_KEY = '@purchased_documents';
const DOWNLOADS_STORAGE_KEY = '@downloaded_documents';

export const useDocumentManager = (user) => {
  const [purchasedDocs, setPurchasedDocs] = useState([]);
  const [downloadedDocs, setDownloadedDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(true);

  // Vérifier la connectivité
  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected && state.isInternetReachable);
    });
    return () => unsubscribe();
  }, []);

  // Charger les documents depuis le stockage local
  const loadLocalDocuments = async () => {
    try {
      const [storedDocs, downloadedFiles] = await Promise.all([
        AsyncStorage.getItem(DOCUMENTS_STORAGE_KEY),
        AsyncStorage.getItem(DOWNLOADS_STORAGE_KEY)
      ]);

      if (storedDocs) {
        setPurchasedDocs(JSON.parse(storedDocs));
      }
      if (downloadedFiles) {
        setDownloadedDocs(JSON.parse(downloadedFiles));
      }
    } catch (error) {
      console.error('Error loading local documents:', error);
    }
  };

  // Télécharger et stocker un document
  const downloadDocument = async (document) => {
    try {
      const documentDir = `${FileSystem.documentDirectory}documents/`;
      const fileName = `${document.id}_${new Date().getTime()}.pdf`;
      const fileUri = `${documentDir}${fileName}`;

      // Créer le dossier si nécessaire
      const dirInfo = await FileSystem.getInfoAsync(documentDir);
      if (!dirInfo.exists) {
        await FileSystem.makeDirectoryAsync(documentDir, { intermediates: true });
      }

      // Télécharger le fichier
      const downloadResult = await FileSystem.downloadAsync(document.url, fileUri);

      if (downloadResult.status === 200) {
        // Mettre à jour le stockage local
        const updatedDownloads = {
          ...downloadedDocs,
          [document.id]: {
            localUri: fileUri,
            fileName,
            downloadDate: new Date().toISOString(),
            size: downloadResult.size
          }
        };

        setDownloadedDocs(updatedDownloads);
        await AsyncStorage.setItem(DOWNLOADS_STORAGE_KEY, JSON.stringify(updatedDownloads));

        return fileUri;
      }
      throw new Error('Download failed');
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  };

  // Ouvrir un document
  const openDocument = async (document) => {
    const docInfo = downloadedDocs[document.id];
    
    if (docInfo) {
      // Vérifier si le fichier existe localement
      const fileInfo = await FileSystem.getInfoAsync(docInfo.localUri);
      if (fileInfo.exists) {
        return docInfo.localUri;
      }
    }

    // Si nous sommes hors ligne et le document n'est pas disponible
    if (!isOnline && !docInfo) {
      throw new Error('Document non disponible hors ligne');
    }

    // Télécharger le document
    return await downloadDocument(document);
  };

  // Synchronisation avec Firebase
  const syncWithFirebase = async () => {
    if (!user || !isOnline) return;

    try {
      const purchasesQuery = query(
        collection(db, 'purchases'),
        where('userId', '==', user.uid)
      );
      
      const snapshot = await getDocs(purchasesQuery);
      const newDocuments = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      // Mettre à jour le stockage local
      await AsyncStorage.setItem(DOCUMENTS_STORAGE_KEY, JSON.stringify(newDocuments));
      setPurchasedDocs(newDocuments);
    } catch (error) {
      console.error('Error syncing with Firebase:', error);
    }
  };

  // Initialisation
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadLocalDocuments();
      if (isOnline) {
        await syncWithFirebase();
      }
      setLoading(false);
    };

    init();
  }, [user, isOnline]);

  return {
    purchasedDocs,
    downloadedDocs,
    loading,
    isOnline,
    downloadDocument,
    openDocument,
    syncWithFirebase
  };
};